/**
 * Copies text to the clipboard and reports whether the write actually happened.
 *
 * The result is load-bearing for callers that do not render the value they copy:
 * with nothing on screen, a confirmation shown on a failed write leaves the user
 * with no way to notice and no way to recover. Never rejects - a caller asking
 * "did it copy?" wants an answer, not an exception.
 *
 * @param text - the string to place on the clipboard
 * @returns true when the write is believed to have happened, false otherwise
 */
export default async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Rejects when the document is unfocused, the gesture is not recognised as
      // user activation, or clipboard-write is denied by permissions policy.
      return false
    }
  }

  // Plain http is not a secure context, so navigator.clipboard is absent and this
  // branch is the only one available - the dev stand always lands here.
  let bufferElement: HTMLSpanElement | null = null
  try {
    bufferElement = document.createElement('span')
    bufferElement.textContent = text
    document.body.appendChild(bufferElement)

    const selection = document.getSelection()
    if (!selection) return false

    const range = document.createRange()
    range.selectNodeContents(bufferElement)
    selection.removeAllRanges()
    selection.addRange(range)

    const copied = document.execCommand('copy')
    selection.removeAllRanges()
    return copied
  } catch {
    return false
  } finally {
    if (bufferElement && bufferElement.parentNode) {
      bufferElement.parentNode.removeChild(bufferElement)
    }
  }
}
